import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
	Form,
	Input,
	Select,
	Button,
	message,
	Row,
	Col,
	Card,
	DatePicker,
	Spin,
	notification,
	Checkbox,
} from "antd";
import DefaultLayout from "../../components/layout/DefaultLayout";
import commonCrud from "../../services/commonCrud";
import userAccessService from "../../services/userAccess";
import dayjs from "dayjs";

const { Option } = Select;
const { TextArea } = Input;

export default function UserAccessForm() {
	const [form] = Form.useForm();
	const navigate = useNavigate();

	const [loading, setLoading] = useState(false);
	const [formLoading, setFormLoading] = useState(false);
	const [users, setUsers] = useState([]);
	const [departments, setDepartments] = useState([]);
	const [locations, setLocations] = useState([]);
	const [edit, setEdit] = useState(null);
	const [isDraft, setIsDraft] = useState(false);
	const [availableUsers, setAvailableUsers] = useState([]);
	const { currentUser } = useSelector((state) => state.user);
	const defaultLocationId = currentUser ? currentUser.location.id : null;
	const defaultDepartmentId = currentUser ? currentUser.department.id : null;

	const allowedPermissions = currentUser?.["roles.permission"]?.map(
		(permission) => permission.name,
	);
	const hasPermission = (permission) =>
		allowedPermissions?.includes(permission);

	const defaultUserId = currentUser ? currentUser.user_id : null;
	const canEditAllRecords = hasPermission("UserAccessForm-editAllRecords");

	const { id } = useParams();
	let initiatorUser = false;
	initiatorUser =
		edit?.created_by?.id != null &&
		currentUser?.user_id != null &&
		edit.created_by.id === currentUser.user_id;

	// Access type options
	const accessTypeOptions = [
		{ value: "internet_access", label: "Internet Access" },
		{ value: "usb_access", label: "USB Access" },
		{ value: "vpn", label: "VPN" },
		{ value: "shared_folder", label: "Shared Folder" },
	];

	// Access rights required options
	const accessRightsRequiredOptions = [
		{ value: "email_id", label: "Email Id" },
		{ value: "email_group", label: "Email Group" },
	];

	// Access creation options
	const accessCreationOptions = [
		{ value: "user_id_created", label: "User ID / VPN ID created" },
		{ value: "access_rights_updated", label: "Access Rights Updated" },
		{ value: "email_created", label: "Email / Group created" },
	];

	// State to manage which checkboxes are checked
	const [checkedAccessItems, setCheckedAccessItems] = useState({
		user_id_created: false,
		access_rights_updated: false,
		email_created: false,
	});

	// State to manage the input values for each checkbox
	const [accessInputValues, setAccessInputValues] = useState({
		user_id_created: "",
		access_rights_updated: "",
		email_created: "",
	});

	// State for Windows Domain Login ID checkbox
	const [windowsDomainLoginId, setWindowsDomainLoginId] = useState(false);

	// --- Data Fetching Functions ---
	const fetchUsers = async () => {
		try {
			const response = await commonCrud.users("all");
			setUsers(response || []);
			return response || [];
		} catch (error) {
			console.error("Error fetching users:", error);
			return [];
		}
	};

	const fetchDepartments = async () => {
		try {
			const response = await commonCrud.departments();
			setDepartments(response || []);
		} catch (error) {
			console.error("Error fetching departments:", error);
		}
	};
	const fetchLocations = async () => {
		try {
			const response = await commonCrud.locations();
			setLocations(response || []);
		} catch (error) {
			console.error("Error fetching locations:", error);
		}
	};

	useEffect(() => {
		if (!canEditAllRecords && currentUser) {
			form.setFieldsValue({
				users: defaultUserId ? [Number(defaultUserId)] : [],
				department_id: defaultDepartmentId ? Number(defaultDepartmentId) : null,
				location_id: defaultLocationId ? Number(defaultLocationId) : null,
			});
		}
	}, [canEditAllRecords, currentUser, form, defaultUserId, defaultDepartmentId, defaultLocationId]);
	const getFormDetails = useCallback(
		async (recordId, fetchedUsers) => {
			try {
				setFormLoading(true);
				const record = await userAccessService.getUserAccessDetails(recordId);
				setEdit(record);

				const combinedUsers = [...fetchedUsers];

				if (record.usersData && Array.isArray(record.usersData)) {
					record.usersData.forEach((userFromData) => {
						if (!combinedUsers.some((user) => Number(user.id) === Number(userFromData.id))) {
							combinedUsers.push(userFromData);
						}
					});
				}

				setUsers(combinedUsers);
				setAvailableUsers(combinedUsers);
				form.resetFields();

				const formValues = {
					request_title: record.request_title,
					users: record.usersData
						? record.usersData.map((user) => Number(user.id))
						: [],
					department_id: record.department?.id ? Number(record.department?.id) : null,
					location_id: record.location?.id ? Number(record.location?.id) : null,
					other: record.other,
					usage_remarks: record.usage_remarks,
					access_type: record.access_type,
					start_date: record.start_date ? dayjs(record.start_date) : null,
					end_date: record.end_date ? dayjs(record.end_date) : null,
					business_requirements: record.business_requirements,
					access_required: record.access_required || [],
					access_rights_required: record.access_rights_required || [],
					access_provided_on: record.access_provided_on
						? dayjs(record.access_provided_on, "YYYY-MM-DD")
						: null,
					its_remarks: record.its_remarks || "",
					windows_domain_login_id_form: record.windows_domain_login_id || false,
				};

				setWindowsDomainLoginId(record.windows_domain_login_id || false);

				if (record.access_creation_details) {
					const details = record.access_creation_details;
					const checkedItems = {
						user_id_created: !!details.user_id_details,
						access_rights_updated: !!details.access_rights_details,
						email_created: !!details.email_details,
					};
					setCheckedAccessItems(checkedItems);

					const inputValues = {
						user_id_created: details.user_id_details || "",
						access_rights_updated: details.access_rights_details || "",
						email_created: details.email_details || "",
					};
					setAccessInputValues(inputValues);
				}

				setTimeout(() => {
					form.setFieldsValue(formValues);
				}, 100);
			} catch (error) {
				console.error("Failed to fetch form details:", error);
				notification.error({
					message: "Error",
					description: "Failed to load form details",
				});
			} finally {
				setFormLoading(false);
			}
		},
		[id, form],
	);

	useEffect(() => {
		const fetchInitialData = async () => {
			try {
				setFormLoading(true);
				const fetchedUsers = await fetchUsers();
				await fetchDepartments();
				await fetchLocations();

				if (id) {
					await getFormDetails(id, fetchedUsers);
				}
			} catch (error) {
				console.error("Failed to fetch initial data:", error);
				notification.error({
					message: "Error",
					description: "Failed to load form data",
				});
			} finally {
				setFormLoading(false);
			}
		};

		fetchInitialData();
	}, [id, getFormDetails]);

	const handleSuccess = (messageText, description) => {
		setLoading(false);
		form.resetFields();
		setCheckedAccessItems({
			user_id_created: false,
			access_rights_updated: false,
			email_created: false,
		});
		setAccessInputValues({
			user_id_created: "",
			access_rights_updated: "",
			email_created: "",
		});
		setWindowsDomainLoginId(false);

		notification.success({
			message: messageText,
			description,
			duration: 3,
		});

		setTimeout(() => {
			navigate("/dashboard");
		}, 500);
	};

	const handleCheckboxChange = (value, checked) => {
		setCheckedAccessItems((prev) => ({
			...prev,
			[value]: checked,
		}));

		if (!checked) {
			setAccessInputValues((prev) => ({
				...prev,
				[value]: "",
			}));
		}
	};

	const handleInputChange = (value, inputValue) => {
		setAccessInputValues((prev) => ({
			...prev,
			[value]: inputValue,
		}));
	};

	const handleWindowsDomainLoginChange = (e) => {
		setWindowsDomainLoginId(e.target.checked);
	};

	const onFinish = async (values) => {
		console.log("Received values:", values);
		setLoading(true);

		// --- VALIDATION UPDATE: Only mandatory fields are checked via Form.Item rules ---
		// Extra manual check just in case, though Form.Item handles this.
		if (!values.request_title) {
			message.error("Please enter the request title");
			setLoading(false);
			return;
		}

		// --- REMOVED DATE VALIDATION FOR TEMPORARY ACCESS ---
		// All other validations removed as requested to make other fields optional.

		try {
			if (!values.users || values.users.length === 0) {
				values.users = defaultUserId ? [defaultUserId] : [];
			}
			if (!values.department_id) {
				values.department_id = defaultDepartmentId;
			}
			if (!values.location_id) {
				values.location_id = defaultLocationId;
			}
			const formData = new FormData();

			const processedValues = {
				...values,
				start_date: values.start_date
					? values.start_date.format("YYYY-MM-DD")
					: null,
				end_date: values.end_date ? values.end_date.format("YYYY-MM-DD") : null,
				access_provided_on: values.access_provided_on
					? values.access_provided_on.format("YYYY-MM-DD")
					: null,
				windows_domain_login_id: windowsDomainLoginId,
			};

			delete processedValues.duration;

			if (processedValues.access_required) {
				if (!Array.isArray(processedValues.access_required)) {
					processedValues.access_required = [processedValues.access_required];
				}
			} else {
				processedValues.access_required = [];
			}

			if (processedValues.access_rights_required) {
				if (!Array.isArray(processedValues.access_rights_required)) {
					processedValues.access_rights_required = [
						processedValues.access_rights_required,
					];
				}
			} else {
				processedValues.access_rights_required = [];
			}

			if (processedValues.users) {
				if (!Array.isArray(processedValues.users)) {
					processedValues.users = [processedValues.users];
				}
			} else {
				processedValues.users = [];
			}

			processedValues.access_creation_details = {
				user_id_details: checkedAccessItems.user_id_created
					? accessInputValues.user_id_created
					: null,
				access_rights_details: checkedAccessItems.access_rights_updated
					? accessInputValues.access_rights_updated
					: null,
				email_details: checkedAccessItems.email_created
					? accessInputValues.email_created
					: null,
			};

			for (const key in processedValues) {
				if (Array.isArray(processedValues[key])) {
					processedValues[key].forEach((item, index) => {
						formData.append(`${key}[]`, item);
					});
				} else if (
					typeof processedValues[key] === "object" &&
					processedValues[key] !== null
				) {
					formData.append(key, JSON.stringify(processedValues[key]));
				} else if (typeof processedValues[key] === "boolean") {
					formData.append(key, processedValues[key].toString());
				} else {
					formData.append(key, processedValues[key] || "");
				}
			}

			const draftValue = isDraft !== null ? isDraft.toString() : "false";
			formData.append("save_as_draft", draftValue);

			let response;
			if (edit?.id) {
				response = await userAccessService.updateUserAccess(edit.id, formData);
			} else {
				response = await userAccessService.postUserAccess(formData);
			}

			if (response?.success) {
				handleSuccess(
					isDraft ? "Draft Saved" : "User Access Request Submitted",
					response.message,
				);
				return;
			}
			throw new Error(response?.message || "Failed to save");
		} catch (error) {
			const description = error?.response?.data?.message || error.message;
			notification.error({
				message: "Error",
				description: description,
			});
		} finally {
			setLoading(false);
		}
	};

	const disabledDate = (current) => {
		return current && current < dayjs().startOf("day");
	};

	const [selectedAccessType, setSelectedAccessType] = useState(null);

	return (
		<DefaultLayout>
			<Spin spinning={formLoading}>
				<Form
					form={form}
					onFinish={onFinish}
					layout='vertical'
					className='mb-50'
				>
					<Card title='User Access Request'>
						<Row gutter={[24, 24]}>
							<Col span={24}>
								<Form.Item
									label='Request Title'
									name='request_title'
									// MANDATORY FIELD
									rules={[
										{
											required: true,
											message: "Please enter the request title",
										},
									]}
								>
									<Input
										size='large'
										placeholder='Enter a descriptive title for this access request'
									/>
								</Form.Item>
							</Col>
						</Row>

						<Row gutter={[24, 24]}>
							<Col lg={12} md={12} sm={24} xs={24}>
								<Form.Item
									label='Users'
									name='users'
									rules={[{ required: false }]}
								>
									<Select
										// mode='multiple'
										showSearch
										optionFilterProp='children'
										size='large'
										placeholder='Select users'
										// disabled={!canEditAllRecords}
										filterOption={(input, option) =>
											option.children
												.toLowerCase()
												.indexOf(input.toLowerCase()) >= 0
										}
									>
										{users.map((user) => (
											<Option key={user.id} value={Number(user.id)}>
												{user.name || user.email}
											</Option>
										))}
									</Select>
								</Form.Item>
							</Col>

							<Col lg={12} md={12} sm={24} xs={24}>
								<Form.Item
									label='Department'
									name='department_id'
									// MANDATORY FIELD
									rules={[
										{ required: true, message: "Please select a department" },
									]}
									initialValue={defaultDepartmentId}
								>
									<Select
										showSearch
										optionFilterProp='children'
										size='large'
										placeholder='Select department'
										// disabled={!canEditAllRecords}
									>
										{departments.map((dept) => (
											<Option key={dept.id} value={Number(dept.id)}>
												{dept.name}
											</Option>
										))}
									</Select>
								</Form.Item>
							</Col>
						</Row>

						<Row gutter={[24, 24]}>
							<Col lg={12} md={12} sm={24} xs={24}>
								<Form.Item
									name='location_id'
									label='Location'
									// MANDATORY FIELD
									rules={[
										{ required: true, message: "Please select location" },
									]}
									initialValue={defaultLocationId}
								>
									<Select
										showSearch
										optionFilterProp='children'
										size='large'
										placeholder='Select location'
										// disabled={!canEditAllRecords}
										filterOption={(input, option) =>
											option.children
												.toLowerCase()
												.indexOf(input.toLowerCase()) >= 0
										}
									>
										{locations.map((loc) => (
											<Option key={loc.id} value={Number(loc.id)}>
												{loc.name}
											</Option>
										))}
									</Select>
								</Form.Item>
							</Col>

							<Col lg={12} md={12} sm={24} xs={24}>
								<Form.Item label='Other' name='other'>
									<Input
										size='large'
										placeholder='Optional additional information'
									/>
								</Form.Item>
							</Col>
						</Row>

						{/* Access Type and Duration Fields - OPTIONAL */}
						<Row gutter={[24, 24]}>
							<Col lg={6} md={6} sm={12} xs={24}>
								<Form.Item label='Access Type' name='access_type'>
									<Select
										size='large'
										placeholder='Select access type'
										onChange={(value) => setSelectedAccessType(value)}
									>
										<Option value='permanent'>Permanent</Option>
										<Option value='temporary'>Temporary</Option>
									</Select>
								</Form.Item>
							</Col>

							<Col lg={6} md={6} sm={12} xs={24}>
								<Form.Item
									label='Start Date'
									name='start_date'
									rules={[
										{
											required: selectedAccessType === "temporary",
											message: "Start Date is required for Temporary access",
										},
									]}
								>
									<DatePicker
										size='large'
										style={{ width: "100%" }}
										disabledDate={disabledDate}
										placeholder='Start Date'
									/>
								</Form.Item>
							</Col>
							<Col lg={6} md={6} sm={12} xs={24}>
								<Form.Item
									label='End Date'
									name='end_date'
									rules={[
										{
											required: selectedAccessType === "temporary",
											message: "End Date is required for Temporary access",
										},
									]}
								>
									<DatePicker
										size='large'
										style={{ width: "100%" }}
										disabledDate={disabledDate}
										placeholder='End Date'
									/>
								</Form.Item>
							</Col>
						</Row>

						<Row gutter={[24, 24]}>
							<Col span={24}>
								<Form.Item label='Usage & Remarks' name='usage_remarks'>
									<TextArea
										rows={4}
										placeholder='Describe the purpose and usage of the access'
									/>
								</Form.Item>
							</Col>
						</Row>

						<Row gutter={[24, 24]}>
							<Col span={24}>
								<Form.Item
									label='Business Requirements'
									name='business_requirements'
								>
									<TextArea
										rows={4}
										placeholder='Explain the business justification for this access'
									/>
								</Form.Item>
							</Col>
						</Row>

						<Row gutter={[24, 24]}>
							<Col lg={12} md={12} sm={24} xs={24}>
								<Form.Item label='Access Required' name='access_required'>
									<Select
										mode='multiple'
										size='large'
										placeholder='Select required access types'
										allowClear
									>
										{accessTypeOptions.map((option) => (
											<Option key={option.value} value={option.value}>
												{option.label}
											</Option>
										))}
									</Select>
								</Form.Item>
							</Col>

							<Col lg={12} md={12} sm={24} xs={24}>
								<Form.Item
									label='Access Rights Required'
									name='access_rights_required'
								>
									<Select
										mode='multiple'
										size='large'
										placeholder='Select access rights (optional)'
										allowClear
									>
										{accessRightsRequiredOptions.map((option) => (
											<Option key={option.value} value={option.value}>
												{option.label}
											</Option>
										))}
									</Select>
								</Form.Item>
							</Col>
						</Row>

						<Row gutter={[24, 24]}>
							<Col lg={12} md={12} sm={24} xs={24}>
								<Form.Item
									name='windows_domain_login_id'
									valuePropName='checked'
								>
									<div style={{ paddingTop: "14px" }}>
										<Checkbox
											checked={windowsDomainLoginId}
											onChange={handleWindowsDomainLoginChange}
										>
											<div style={{ fontSize: "14px" }}>
												Windows Domain Login ID
											</div>
										</Checkbox>
									</div>
								</Form.Item>
							</Col>
						</Row>
					</Card>

					{edit?.id &&
						edit.status &&
						!["draft", "return"].includes(
							String(edit.status).toLowerCase(),
						) && (
							<section style={{ marginTop: 24 }} id='filledByITS'>
								<Card
									title='To Be Filled By ITS Department'
									style={{ marginTop: 24 }}
								>
									<Row gutter={[24]}>
										<Col lg={8} md={10} sm={24} xs={24}>
											<Form.Item
												label='Access Provided On'
												name='access_provided_on'
											>
												<DatePicker
													size='large'
													style={{ width: "100%" }}
													disabledDate={disabledDate}
												/>
											</Form.Item>
										</Col>
										<Col lg={16} md={14} sm={24} xs={24}>
											<Form.Item label='ITS Remarks' name='its_remarks'>
												<TextArea
													rows={4}
													placeholder='ITS department remarks and notes'
												/>
											</Form.Item>
										</Col>
										<Col span={24}>
											<Form.Item
												label='Access Creation Actions'
												name='access_creation'
											>
												<div>
													{accessCreationOptions.map((option) => (
														<div
															key={option.value}
															style={{ marginBottom: 16 }}
														>
															<Checkbox
																checked={checkedAccessItems[option.value]}
																onChange={(e) =>
																	handleCheckboxChange(
																		option.value,
																		e.target.checked,
																	)
																}
															>
																{option.label}
															</Checkbox>

															{checkedAccessItems[option.value] && (
																<Input
																	size='large'
																	placeholder='ID/Email'
																	value={accessInputValues[option.value]}
																	onChange={(e) =>
																		handleInputChange(
																			option.value,
																			e.target.value,
																		)
																	}
																	style={{
																		marginLeft: 16,
																		width: "calc(100% - 32px)",
																	}}
																/>
															)}
														</div>
													))}
												</div>
											</Form.Item>
										</Col>
									</Row>
								</Card>
							</section>
						)}

					{/* Form Actions */}
					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							marginTop: 24,
						}}
						className='mt-24'
					>
						<Button
							type='primary'
							block
							disabled={loading}
							loading={loading}
							className='btn-blue mr-20'
							onClick={() => {
								setIsDraft(false);
								form.submit();
							}}
						>
							{edit?.id ? "Update Request" : "Submit Request"}
						</Button>

						{!edit?.id ? (
							<Button
								disabled={loading}
								loading={loading}
								type='primary'
								onClick={() => {
									setIsDraft(true);
									form.submit();
								}}
							>
								Save Draft
							</Button>
						) : initiatorUser ? (
							<Button
								disabled={loading}
								loading={loading}
								type='primary'
								onClick={() => {
									setIsDraft(true);
									form.submit();
								}}
							>
								Save Draft
							</Button>
						) : (
							""
						)}
						<Button
							loading={loading}
							disabled={loading}
							onClick={() => navigate("/dashboard")}
						>
							Cancel
						</Button>
					</div>
				</Form>
			</Spin>
		</DefaultLayout>
	);
}
